import { apiClient } from './apiService'

export interface PMOAssignee {
  id: string
  name: string
  displayName: string
  email?: string
}

export interface PMOAssigneeStats {
  name: string
  total: number
  statuses: Record<string, number>
  priorities: Record<string, number>
}

export interface PMOAssigneeStatsResponse {
  success: boolean
  data: {
    assignees: PMOAssigneeStats[]
    summary: {
      totalInitiatives: number
      withAssignees: number
      withoutAssignees: number
      uniqueAssignees: number
    }
  }
  message: string
  error?: string
}

export interface PMOAssigneeResponse {
  success: boolean
  data: PMOAssignee[]
  total: number
  message: string
  error?: string
}

export interface PMOAssigneeSearchResponse {
  success: boolean
  data: PMOAssignee[]
  total: number
  searchTerm: string
  message: string
  error?: string
}

export interface PMOAssigneeInitiative {
  key: string
  summary: string
  status: string
  priority: string
  pmoAssignee: string
}

export interface PMOAssigneeInitiativesResponse {
  success: boolean
  data: PMOAssigneeInitiative[]
  total: number
  assignee: string
  message: string
  error?: string
}

class PMOAssigneeService {
  private baseUrl = '/pmo-assignees'

  /**
   * Get all PMO assignees
   */
  async getAllPMOAssignees(jql?: string): Promise<PMOAssigneeResponse> {
    try {
      const params = jql ? { jql } : {}
      const response = await apiClient.get<PMOAssigneeResponse>(this.baseUrl, { params })
      return response.data
    } catch (error) {
      console.error('Error fetching PMO assignees:', error)
      return {
        success: false,
        data: [],
        total: 0,
        message: 'Failed to fetch PMO assignees',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get PMO assignee statistics
   */
  async getPMOAssigneeStats(): Promise<PMOAssigneeStatsResponse> {
    try {
      const response = await apiClient.get<PMOAssigneeStatsResponse>(`${this.baseUrl}/stats`)
      return response.data
    } catch (error) {
      console.error('Error fetching PMO assignee stats:', error)
      return {
        success: false,
        data: {
          assignees: [],
          summary: {
            totalInitiatives: 0,
            withAssignees: 0,
            withoutAssignees: 0,
            uniqueAssignees: 0
          }
        },
        message: 'Failed to fetch PMO assignee statistics',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Search PMO assignees by name or email
   */
  async searchPMOAssignees(searchTerm: string): Promise<PMOAssigneeSearchResponse> {
    try {
      if (!searchTerm.trim()) {
        return {
          success: false,
          data: [],
          total: 0,
          searchTerm: searchTerm,
          message: 'Search term cannot be empty',
          error: 'Empty search term'
        }
      }

      const response = await apiClient.get<PMOAssigneeSearchResponse>(`${this.baseUrl}/search`, {
        params: { q: searchTerm.trim() }
      })
      return response.data
    } catch (error) {
      console.error('Error searching PMO assignees:', error)
      return {
        success: false,
        data: [],
        total: 0,
        searchTerm: searchTerm,
        message: 'Failed to search PMO assignees',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Get initiatives for a specific PMO assignee
   */
  async getInitiativesByPMOAssignee(assigneeName: string): Promise<PMOAssigneeInitiativesResponse> {
    try {
      const encodedAssigneeName = encodeURIComponent(assigneeName)
      const response = await apiClient.get<PMOAssigneeInitiativesResponse>(
        `${this.baseUrl}/${encodedAssigneeName}/initiatives`
      )
      return response.data
    } catch (error) {
      console.error('Error fetching initiatives by PMO assignee:', error)
      return {
        success: false,
        data: [],
        total: 0,
        assignee: assigneeName,
        message: 'Failed to fetch initiatives for PMO assignee',
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * Clear PMO assignee cache
   */
  async clearCache(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>(`${this.baseUrl}/cache/clear`)
      return response.data
    } catch (error) {
      console.error('Error clearing PMO assignee cache:', error)
      return {
        success: false,
        message: 'Failed to clear PMO assignee cache'
      }
    }
  }

  /**
   * Filter PMO assignees by search term (client-side)
   */
  filterPMOAssignees(assignees: PMOAssignee[], searchTerm: string): PMOAssignee[] {
    if (!searchTerm.trim()) {
      return assignees
    }

    const term = searchTerm.toLowerCase()
    return assignees.filter(assignee => 
      assignee.name.toLowerCase().includes(term) ||
      assignee.displayName.toLowerCase().includes(term) ||
      assignee.id.includes(term) ||
      (assignee.email && assignee.email.toLowerCase().includes(term))
    )
  }

  /**
   * Get PMO assignee by ID
   */
  getPMOAssigneeById(assignees: PMOAssignee[], id: string): PMOAssignee | undefined {
    return assignees.find(assignee => assignee.id === id)
  }

  /**
   * Get PMO assignee by name
   */
  getPMOAssigneeByName(assignees: PMOAssignee[], name: string): PMOAssignee | undefined {
    return assignees.find(assignee => 
      assignee.name.toLowerCase() === name.toLowerCase() ||
      assignee.displayName.toLowerCase() === name.toLowerCase()
    )
  }

  /**
   * Sort PMO assignees by name
   */
  sortPMOAssigneesByName(assignees: PMOAssignee[], ascending: boolean = true): PMOAssignee[] {
    return [...assignees].sort((a, b) => {
      const comparison = a.name.localeCompare(b.name)
      return ascending ? comparison : -comparison
    })
  }

  /**
   * Sort PMO assignees by total initiatives (requires stats data)
   */
  sortPMOAssigneesByTotal(assigneeStats: PMOAssigneeStats[], ascending: boolean = false): PMOAssigneeStats[] {
    return [...assigneeStats].sort((a, b) => {
      const comparison = a.total - b.total
      return ascending ? comparison : -comparison
    })
  }

  /**
   * Get PMO assignee workload distribution
   */
  getWorkloadDistribution(assigneeStats: PMOAssigneeStats[]): {
    assignee: string
    total: number
    percentage: number
  }[] {
    const totalInitiatives = assigneeStats.reduce((sum, assignee) => sum + assignee.total, 0)
    
    return assigneeStats.map(assignee => ({
      assignee: assignee.name,
      total: assignee.total,
      percentage: totalInitiatives > 0 ? Math.round((assignee.total / totalInitiatives) * 100) : 0
    }))
  }

  /**
   * Get status distribution for a specific PMO assignee
   */
  getStatusDistribution(assigneeStats: PMOAssigneeStats, assigneeName: string): {
    status: string
    count: number
    percentage: number
  }[] {
    const total = assigneeStats.total
    
    return Object.entries(assigneeStats.statuses).map(([status, count]) => ({
      status,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0
    })).sort((a, b) => b.count - a.count)
  }

  /**
   * Get priority distribution for a specific PMO assignee
   */
  getPriorityDistribution(assigneeStats: PMOAssigneeStats, assigneeName: string): {
    priority: string
    count: number
    percentage: number
  }[] {
    const total = assigneeStats.total
    
    return Object.entries(assigneeStats.priorities).map(([priority, count]) => ({
      priority,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0
    })).sort((a, b) => b.count - a.count)
  }
}

export const pmoAssigneeService = new PMOAssigneeService()
export default pmoAssigneeService
